import React from "react";

/**
 * GameplayBrandCorner — compact brand block anchored to the top-left of the
 * GamePlay surface (VS Tempo / VS Robot / VS Humano / Competition /
 * Eliminatorias). Mirrors the HomeHeader logo + brand-text look 1:1 so the
 * brand identity is consistent across the whole app.
 *
 * Renders:
 *   • the configured logo ring (thickness / distance / colour / animation)
 *   • the mascot image inside the ring
 *   • two lines of brand text — `logo_name` (localised) and `logo_tagline`
 *
 * The whole block is a link to "/". Sized to fit inside gameplay chrome
 * without stealing space from the roulette + card + options grid.
 */
const OUTLINE_OFFSETS = [
  "1px 0 0", "-1px 0 0", "0 1px 0", "0 -1px 0",
  "1px 1px 0", "-1px -1px 0", "1px -1px 0", "-1px 1px 0",
];

function textLineStyle(s, defaults) {
  const st = { ...defaults, ...(s || {}) };
  const shadows = [];
  if (st.outline !== false) {
    OUTLINE_OFFSETS.forEach((o) => shadows.push(`${o} ${st.outline_color || "#0A0E2A"}`));
  }
  if (st.shadow !== false) shadows.push("0 2px 2px rgba(0,0,0,0.35)");
  return {
    fontSize: st.size,
    color: st.color,
    fontWeight: st.weight,
    letterSpacing: `${st.letter_spacing}px`,
    textTransform: st.uppercase ? "uppercase" : "none",
    WebkitTextStroke: st.outline !== false ? `${st.stroke_width || 0.6}px ${st.outline_color || "#0A0E2A"}` : undefined,
    textShadow: shadows.length ? shadows.join(",") : undefined,
  };
}

export default function GameplayBrandCorner({ cfg = {}, lang = "pt" }) {
  const gb = cfg.gameplay_brand || {};
  if (gb.enabled === false) return null;
  const r = cfg.logo_ring || {};
  const enabled = r.enabled !== false;
  const size = Math.max(24, Math.min(80, Number(gb.logo_size) || Number(r.logo_size) || 42));
  const thick = r.thickness ?? 3;
  const dist = r.distance ?? 2;
  const outer = size + 2 * (thick + dist);

  let ringBg = r.color_primary || "#000";
  if (r.style === "rainbow") {
    const c1 = r.color_primary || "#000000";
    const c2 = r.color_glow || "#FF00C8";
    const c3 = r.color_secondary || "#FFD93D";
    ringBg = `conic-gradient(from 0deg,${c1},${c2},${c3},${c2},${c1})`;
  } else if (r.style === "gradient") {
    ringBg = `linear-gradient(135deg,${r.color_primary || "#000"},${r.color_secondary || "#FFD93D"})`;
  }
  const ringStyle = {
    width: outer,
    height: outer,
    padding: thick,
    background: ringBg,
    borderRadius: "9999px",
  };
  const dir = r.direction || "normal";
  if ((r.intensity || 0) > 0 || r.animation === "glow") {
    const i = r.intensity || 25;
    ringStyle.boxShadow = `0 0 ${i / 2}px ${i / 4}px ${r.color_glow || "#FF00C8"}`;
  }
  if (r.animation === "rotate") ringStyle.animation = `brand-spin ${r.speed || 6}s linear infinite ${dir}`;
  if (r.animation === "pulse") ringStyle.animation = `brand-pulse ${r.speed || 6}s ease-in-out infinite`;
  if (r.style === "dashed") {
    ringStyle.background = "transparent";
    ringStyle.border = `${thick}px dashed ${r.color_primary || "#000"}`;
    ringStyle.padding = 0;
  }

  const inner = cfg.logo_url ? (
    <img
      src={cfg.logo_url}
      alt="logo"
      className="w-full h-full object-cover rounded-full bg-white"
      loading="eager"
      decoding="async"
      draggable={false}
      data-testid="gameplay-brand-image"
    />
  ) : (
    <div className="w-full h-full rounded-full bg-white" aria-hidden="true" />
  );

  const brandName =
    gb.name?.[lang] || gb.name?.pt || cfg.logo_name?.[lang] || cfg.logo_name?.pt || "";
  const brandTagline =
    gb.tagline?.[lang] || gb.tagline?.pt || cfg.logo_tagline?.[lang] || cfg.logo_tagline?.pt || "";

  const nameStyle = textLineStyle(gb.name_style, {
    color: "#FFFFFF", size: 15, weight: 900, letter_spacing: -0.3,
    uppercase: false, outline: true, outline_color: "#0A0E2A", shadow: true,
  });
  const taglineStyle = textLineStyle(gb.tagline_style, {
    color: "#FFD93D", size: 9, weight: 700, letter_spacing: 2,
    uppercase: true, outline: true, outline_color: "#0A0E2A", shadow: true, stroke_width: 0.5,
  });

  return (
    <div
      className="absolute top-3 left-3 z-40"
      data-testid="gameplay-logo-corner"
    >
      <a
        href="/"
        title="Início"
        data-testid="gameplay-logo-link"
        className="flex items-center gap-2 group rounded-full px-1 py-1 transition-transform hover:scale-[1.02]"
        style={{ background: "transparent" }}
      >
        {enabled ? (
          <div style={ringStyle} className="group-hover:scale-105 transition-transform shrink-0">
            <div
              style={{
                padding: dist,
                background: "white",
                borderRadius: "9999px",
                width: "100%",
                height: "100%",
              }}
            >
              {inner}
            </div>
          </div>
        ) : (
          <div
            style={{ width: size, height: size }}
            className="rounded-full overflow-hidden bg-white shadow-sm group-hover:scale-105 transition-transform shrink-0"
          >
            {inner}
          </div>
        )}
        {(brandName || brandTagline) && (
          <div className="flex flex-col leading-tight pr-1" data-testid="gameplay-brand-text">
            {brandName && (
              <span
                className="font-[Fredoka]"
                style={nameStyle}
                data-testid="gameplay-brand-name"
              >
                {brandName}
              </span>
            )}
            {brandTagline && (
              <span
                style={taglineStyle}
                data-testid="gameplay-brand-tagline"
              >
                {brandTagline}
              </span>
            )}
          </div>
        )}
      </a>
    </div>
  );
}
